
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/auth/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shield, Phone, KeyRound, ChevronLeft, AlertCircle, Loader2, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getApiBaseUrl } from "@/lib/apiBase";
import { normalizeUserRecord } from "@/lib/userDisplayName";
import { writeAuthToken, parseVerifyOtpResponse } from "@/lib/authStorage";

// חייב להסתיים ב-/api – ראה server/src/app.js; בלי זה POST נוחת על /auth/... ומקבל 404
const API_BASE = getApiBaseUrl();

function pickLanding(user) {
  const roleKey = user?.role?.key || user?.role;
  
  // אם זו מנהלת מבצעים, שלח אותה ישירות למבצעים
  if (roleKey === 'PROMOTIONS_ADMIN') {
    return createPageUrl("Promotions");
  }
  
  // לכל השאר, דף הבית הרגיל
  return createPageUrl("Home");
}

export default function LoginOtp() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const queryClient = useQueryClient();

  const [step, setStep] = useState("phone"); // phone, code
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const codeInputRef = useRef(null);

  const focusCodeInput = useCallback(() => {
    const el = codeInputRef.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, []);

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_BASE,
      headers: { "Content-Type": "application/json" },
    });
  }, []);

  // ניהול טיימר לשליחה חוזרת
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // פוקוס מיד אחרי שה-DOM מתעדכן (לפני ציור המסך) — אמין יותר מ-useEffect+setTimeout
  useLayoutEffect(() => {
    if (step !== "code") return;
    focusCodeInput();
  }, [step, focusCodeInput]);

  // גיבוי: אנימציה / מובייל / iOS לפעמים דוחים פוקוס עד אחרי סיום מעבר
  useEffect(() => {
    if (step !== "code") return;
    const t1 = window.setTimeout(() => focusCodeInput(), 0);
    const t2 = window.setTimeout(() => focusCodeInput(), 150);
    const t3 = window.setTimeout(() => focusCodeInput(), 400);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [step, focusCodeInput]);

  const handlePhoneSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");

    if (!phoneNumber || phoneNumber.length < 9) {
      setError("אנא הזן מספר טלפון תקין");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/request-otp", { phone: phoneNumber });
      setStep("code");
      setCountdown(30); // התחלת ספירה לאחור לשליחה חוזרת
    } catch (e) {
      const raw = e?.response?.data;
      const d = typeof raw === "string" ? null : raw;
      const htmlHint =
        typeof raw === "string" && /cannot\s+post/i.test(raw)
          ? "נראה שהשרת על הפורט לא מכיר את /api/auth (או שזה לא השרת של הפרויקט). הרץ: lsof -i :5001 ובדוק GET /api/auth/health"
          : null;
      const code = d?.error;
      const map = {
        user_not_found: d?.message || "מספר הטלפון לא רשום במערכת.",
        user_blocked: d?.message || "משתמש חסום לכניסה.",
        too_many_requests: d?.message || "נסה שוב בעוד רגע.",
        route_not_found:
          d?.message ||
          "השרת לא מכיל את נתיב ההתחברות. ודא שרצת npm start מתוך תיקיית server ושאין תהליך אחר על פורט 5001.",
      };
      const msg =
        htmlHint ||
        (code && map[code]) ||
        d?.message ||
        (typeof d?.error === "string" && d.error !== "Not Found" ? d.error : null) ||
        (e?.response?.status === 404
          ? "בקשה נדחתה (404). פתח בדפדפן: /api/auth/health — אם אין JSON עם ok:true, זה לא ה-API הנכון על הפורט."
          : "שגיאה בשליחת הקוד");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    handlePhoneSubmit();
  };

  const handleCodeSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (!code || code.length < 6) {
      setError("אנא הזן קוד אימות מלא");
      return;
    }
  
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { 
        phone: phoneNumber, 
        code: code 
      });
  
      const parsed = parseVerifyOtpResponse(data);
      if (!parsed.token) {
        const msg =
          parsed.reason === "html"
            ? "השרת החזיר דף אתר (HTML) במקום תשובת API. יש להגדיר ב־nginx העברת proxy מנתיב api לשרת ה־API; אל תנתב את api לקבצי האפליקציה."
            : parsed.reason === "invalid_json"
              ? "תשובת השרת אינה JSON תקין."
              : "התקבלה תשובה לא תקינה מהשרת (חסר טוקן). נסה שוב או פנה לתמיכה.";
        setError(msg);
        return;
      }

      writeAuthToken(parsed.token);

      const userPayload = normalizeUserRecord(parsed.user);
      if (userPayload && typeof userPayload === "object") {
        localStorage.setItem("app_user", JSON.stringify(userPayload));
      } else {
        localStorage.removeItem("app_user");
      }

      await queryClient.cancelQueries();
      queryClient.clear();

      const landingPage = pickLanding(userPayload || {});
      window.location.href = landingPage;

    } catch (e) {
      const d = e?.response?.data;
      const msg =
        d?.message ||
        (d?.error === "invalid_code" ? "קוד שגוי או שפג תוקף." : null) ||
        (typeof d?.error === "string" ? d.error : null) ||
        "קוד לא תקין. נסה שוב.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-slate-50 to-slate-100 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-20 w-72 h-72 bg-red-300/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-96 h-96 bg-slate-300/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-10"
      >
        <Card className="shadow-2xl border-0 overflow-hidden backdrop-blur-sm bg-white/95">
          {/* Header gradient bar */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-600 via-slate-500 to-red-500" />

          <CardHeader className="text-center pb-4 pt-8">
  <motion.div
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{
      type: "spring",
      stiffness: 260,
      damping: 20,
      delay: 0.2,
    }}
    // כאן הסרנו את הרקע, הצל והגבולות המרובעים
    className="mx-auto mb-2 flex items-center justify-center"
  >
    <img
      src="/keshetLogo-removebg-preview.png"
      alt="קשת גיבורים"
      className="w-52 max-w-full h-auto object-contain"
    />
  </motion.div>

  <p className="text-gray-500 mt-2">
    {step === "phone" ? "התחברות למערכת" : "הזן את הקוד שנשלח אליך"}
  </p>
</CardHeader>

          <CardContent className="p-6">
            <AnimatePresence mode="wait">
              {step === "phone" ? (
                <motion.form
                  key="phone"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onSubmit={handlePhoneSubmit}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-red-600" />
                      מספר טלפון נייד
                    </label>
                    <div className="relative">
                      <Input
                        type="tel"
                        placeholder="05X-XXXXXXX"
                        value={phoneNumber}
                        onChange={(e) => {
                          setPhoneNumber(e.target.value);
                          setError("");
                        }}
                        className="pr-12 h-14 text-lg border-2 focus:border-red-500 transition-colors"
                        dir="ltr"
                      />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <Phone className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  {/* {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm"
                    >
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )} */}

{error && (
  <motion.div
    key="phone-error-msg" // זה המפתח שמונע את הקריסה במעבר שלבים
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm"
  >
    <AlertCircle className="w-4 h-4 flex-shrink-0" />
    <span>{error}</span>
  </motion.div>
)}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-14 bg-gradient-to-r from-red-600 to-slate-500 hover:from-red-700 hover:to-slate-600 text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                        שולח קוד...
                      </>
                    ) : (
                      <>
                        <ChevronLeft className="w-5 h-5 ml-2" />
                        שלח קוד התחברות
                      </>
                    )}
                  </Button>
                </motion.form>
              ) : (
                <motion.form
                  key="code"
                  initial={{ x: 16 }}
                  animate={{ x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.2 }}
                  onAnimationComplete={focusCodeInput}
                  onSubmit={handleCodeSubmit}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <label
                      htmlFor="login-otp-code"
                      className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-red-600" />
                      קוד אימות
                    </label>
                    <div className="relative">
                      <Input
                        id="login-otp-code"
                        ref={codeInputRef}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                        enterKeyHint="done"
                        autoFocus
                        placeholder="000000"
                        value={code}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                          setCode(digits);
                          setError("");
                        }}
                        className="text-center h-16 text-3xl font-bold tracking-widest border-2 focus:border-red-500 transition-colors"
                        maxLength={6}
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      נשלח אל: {phoneNumber}
                    </p>
                  </div>

                  {/* {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm"
                    >
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )} */}

{error && (
  <motion.div
    key="otp-error-msg" // מפתח שונה מהשלב הקודם
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm"
  >
    <AlertCircle className="w-4 h-4 flex-shrink-0" />
    <span>{error}</span>
  </motion.div>
)}

                  <div className="space-y-3">
                    <Button
                      type="submit"
                      disabled={loading || code.length < 6}
                      className="w-full h-14 bg-gradient-to-r from-red-600 to-slate-500 hover:from-red-700 hover:to-slate-600 text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                          מאמת...
                        </>
                      ) : (
                        <>
                          <Shield className="w-5 h-5 ml-2" />
                          התחבר למערכת
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setStep("phone")}
                      className="w-full"
                    >
                      שינוי מספר טלפון
                    </Button>
                  </div>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={countdown > 0 || loading}
                      className={`text-sm font-medium transition-all ${
                        countdown > 0 || loading
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-red-600 hover:text-red-700 font-bold"
                      }`}
                    >
                      {countdown > 0 ? (
                        <span className="flex items-center gap-2 justify-center">
                          <Clock className="w-4 h-4" />
                          שליחה חוזרת בעוד {countdown} שניות
                        </span>
                      ) : (
                        "לא קיבלת קוד? שלח שוב"
                      )}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>

        {/* Footer info box */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-red-100 text-center"
        >
          <p className="text-sm text-gray-600">
            🔒 גישה מוגבלת למשתמשים מורשים בלבד
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}